import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import thumbNailImage from '../../assets/icons/thumbNailImage.svg';
import CropTool from '../../Pages/AssetLibrary/Assets/CropTool';
import {
  getImagesByLocation,
  IMAGE_PICKER_PAGE_SIZE,
  matchesAcceptedExtensions,
  parseAcceptedExtensions,
  PRODUCT_INFORMATION_LOCATION,
} from '../../Pages/AssetLibrary/Assets/store/asset.service';
import { useAssetPreviewUrl } from '../../Pages/AssetLibrary/Assets/useAssetPreviewUrl';
import { useS3Url } from '../../Pages/AssetLibrary/Assets/useS3Url';
import { getS3FileAsBlob } from '../../services/s3Services';
import { toasterMsg } from '../Toaster/Toaster';
import { BombiixModal } from '../BombiixModal';
import { CropIcon } from 'bombiix-icon';
import { NewCheckbox } from '../PIM/common/NewCheckbox';
import './ImageUploadModal.css';

interface Asset {
  _id: string;
  title?: string;
  description?: string;
  fileUrl?: string;
  croppedFileUrl?: string;
  fileType?: string;
}

export interface ImageUploadData {
  file: File | null;
  assetUrl: string | null;
  imageTitle: string;
  aliasName: string;
  assetKey?: string | null;
  /** _id of the Asset document selected from the asset library, when isAssetUpload is true. */
  assetId?: string | null;
  isAssetUpload?: boolean;
  /** Ticked "Add to Design Detail"; only ever true when the caller opted into the checkbox. */
  isIncludeDesign?: boolean;
  /**
   * True when the "Add to Design/Details" tick was set alongside the chosen image. Only ever true
   * when the host passed `showAddToDesign`; callers forward it to the product payload as
   * `isIncludeDesign`, which is what copies the saved image into the product's design canvas.
   */
  addToDesign?: boolean;
  /**
   * True when the "Add to Asset Library" tick was set alongside an uploaded image. Only ever true
   * when the host passed `showAddToAssetLibrary` and the image came from the device: an image
   * picked from the library is already an asset. Callers forward it to the product payload as
   * `isAddToAssetLibrary`, which is what makes the backend store the upload under the Asset
   * Library prefix and file it as an asset instead of keeping it on the product's own prefix.
   */
  addToAssetLibrary?: boolean;
}

interface ImageUploadModalProps {
  show: boolean;
  productId?: string | null;
  /**
   * Override the asset fetch. When provided, productId is ignored. Called once per page, with the
   * same allowedImageExtensions the default fetch forwards so the override can narrow the query
   * server-side identically, and must return the server's page unfiltered so paging stays correct.
   */
  fetchImages?: (search: string, page: number, limit: number, allowedImageExtensions?: string) => Promise<any>;
  /**
   * Comma-separated accept list (e.g. '.jpg, .png'). The fetch sends it to the server so only
   * matching assets are returned; fetched assets whose fileUrl/croppedFileUrl still don't match —
   * the legacy rows whose S3 key carries no extension — are excluded from the results.
   */
  allowedImageExtensions?: string;
  /**
   * Renders the "Add to Design/Details" checkbox under the preview. Off by default so the modal's
   * other hosts (BOM, care labels, specifications) are unaffected — only the product image upload
   * feeds a design canvas.
   */
  showAddToDesign?: boolean;
  /** Shows the Asset Library search panel. On by default; BOM V2 Library uploads turn it off (plain upload only). */
  showAssetLibrary?: boolean;
  /**
   * Renders the "Add to Asset Library" checkbox under the preview. Off by default so the modal's
   * other hosts are unaffected — only the hosts whose save endpoint understands
   * `isAddToAssetLibrary` can honour the tick.
   */
  showAddToAssetLibrary?: boolean;
  /**
   * The image the host already holds — an S3 key, an http(s)/blob URL or a base64 data URL. Shown
   * as the preview (and offered to the crop tool) until the user picks or uploads a replacement.
   * Saving without a replacement sends no file and no asset, so the host keeps it unchanged.
   */
  existingImage?: string | null;
  onClose: () => void;
  onSave: (data: ImageUploadData) => void;
}

interface AssetThumbProps {
  asset: Asset;
  isSelected: boolean;
  onSelect: (asset: Asset, url: string | null) => void;
}

/**
 * Renders one asset tile. The `image` asset category covers formats no browser
 * can decode (TIFF, PSD, HEIC), so the thumbnail is attempted only for types
 * that paint in an <img>; everything else — and any decode failure — falls back
 * to the icon tile rather than the browser's broken-image glyph.
 */
function AssetThumb({ asset, isSelected, onSelect }: AssetThumbProps) {
  const previewUrl = useAssetPreviewUrl(asset._id, asset.fileType, asset.croppedFileUrl || asset.fileUrl || null);
  const [previewError, setPreviewError] = useState(false);

  useEffect(() => {
    setPreviewError(false);
  }, [previewUrl]);

  const showThumbnail = Boolean(previewUrl) && !previewError;

  return (
    <div className={`ium-asset-thumb${isSelected ? ' ium-asset-thumb--selected' : ''}`} onClick={() => onSelect(asset, previewUrl)} title={asset.title}>
      {showThumbnail ? (
        <img src={previewUrl as string} alt={asset.title} onError={() => setPreviewError(true)} />
      ) : (
        <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-light">
          <span className="icon-image-placeholder f-16 text-muted" />
        </div>
      )}
    </div>
  );
}

function ImageUploadModal({
  show,
  productId,
  fetchImages,
  allowedImageExtensions,
  showAddToDesign,
  showAddToAssetLibrary,
  existingImage,
  showAssetLibrary = true,
  onClose,
  onSave,
}: ImageUploadModalProps) {
  const { t } = useTranslation('assetLibraryAssets');
  const acceptedExtensions = useMemo(() => parseAcceptedExtensions(allowedImageExtensions), [allowedImageExtensions]);
  const isExistingImageInline = isDataUrl(existingImage);
  const signedExistingImageUrl = useS3Url(show && !isExistingImageInline ? existingImage : null);
  const existingImageUrl = isExistingImageInline ? (existingImage as string) : signedExistingImageUrl;
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageTitle, setImageTitle] = useState('');
  const [aliasName, setAliasName] = useState('');
  const [assetSearch, setAssetSearch] = useState('');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetPage, setAssetPage] = useState(1);
  const [hasMoreAssets, setHasMoreAssets] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [selectedAssetUrl, setSelectedAssetUrl] = useState<string | null>(null);
  const [selectedAssetKey, setSelectedAssetKey] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [addToDesign, setAddToDesign] = useState(false);
  const [addToAssetLibrary, setAddToAssetLibrary] = useState(false);
  const [isCropLoading, setIsCropLoading] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cropObjectUrlRef = useRef<string | null>(null);
  const originalCropSrcRef = useRef<string | null>(null);
  const originalObjectUrlRef = useRef<string | null>(null);
  const assetRequestIdRef = useRef(0);
  const fetchedAssetCountRef = useRef(0);

  /** Release the object URL created for the crop source, if any. */
  const revokeCropObjectUrl = () => {
    if (cropObjectUrlRef.current) {
      URL.revokeObjectURL(cropObjectUrlRef.current);
      cropObjectUrlRef.current = null;
    }
  };

  /**
   * Drop the retained pre-crop original, releasing its object URL when this modal created one.
   * Called only when the picture itself changes — a new upload, a different asset, a modal reset.
   * Never on a crop, which is the one thing the original exists to undo.
   */
  const revokeOriginalCropSrc = () => {
    if (originalObjectUrlRef.current) {
      URL.revokeObjectURL(originalObjectUrlRef.current);
      originalObjectUrlRef.current = null;
    }
    originalCropSrcRef.current = null;
  };

  /**
   * Keep the image the crop tool was opened with, the first time a crop is applied, so a later
   * re-crop reopens the untouched original instead of narrowing the previous crop further — the
   * previous crop's bytes replace the preview and are otherwise the only thing left to crop.
   *
   * Ownership of the backing object URL moves here for the two URLs this modal creates itself (the
   * locally picked file's preview and the proxy-fetched asset), so callers must not revoke those
   * afterwards. A pre-signed S3 URL belongs to the asset hook and is only referenced.
   */
  const retainOriginalCropSrc = (src: string | null) => {
    if (!src || originalCropSrcRef.current) return;
    originalCropSrcRef.current = src;
    if (cropObjectUrlRef.current === src) {
      originalObjectUrlRef.current = src;
      cropObjectUrlRef.current = null;
      return;
    }
    if (previewUrl === src) originalObjectUrlRef.current = src;
  };

  useEffect(
    () => () => {
      revokeCropObjectUrl();
      revokeOriginalCropSrc();
    },
    [],
  );

  /**
   * Load one page of library assets. Page 1 replaces the list, later pages append to it.
   * A request id guards against an earlier in-flight page overwriting a newer search.
   * `hasMore` is derived from the raw (unfiltered) fetched count against the server total, because
   * the client-side extension filter can shrink a page without meaning the server ran out of rows.
   */
  const fetchAssetPage = useCallback(
    async (pageToLoad: number) => {
      const isFirstPage = pageToLoad === 1;
      const requestId = ++assetRequestIdRef.current;
      if (isFirstPage) setLoading(true);
      else setLoadingMore(true);
      try {
        const res = fetchImages
          ? await fetchImages(assetSearch, pageToLoad, IMAGE_PICKER_PAGE_SIZE, allowedImageExtensions)
          : await getImagesByLocation(PRODUCT_INFORMATION_LOCATION, productId!, assetSearch, allowedImageExtensions, pageToLoad, IMAGE_PICKER_PAGE_SIZE);
        if (requestId !== assetRequestIdRef.current) return;
        const fetchedAssets: Asset[] = res?.data?.assets ?? [];
        const total = Number(res?.data?.total);
        const filteredAssets = fetchedAssets.filter((asset) =>
          matchesAcceptedExtensions({ fileUrl: asset?.croppedFileUrl || asset?.fileUrl || null, fileType: asset?.fileType ?? null }, acceptedExtensions),
        );
        fetchedAssetCountRef.current = isFirstPage ? fetchedAssets.length : fetchedAssetCountRef.current + fetchedAssets.length;
        setAssets((prev) => (isFirstPage ? filteredAssets : [...prev, ...filteredAssets]));
        setAssetPage(pageToLoad);
        const hasTotal = Number.isFinite(total) && total > 0;
        setHasMoreAssets(fetchedAssets.length === IMAGE_PICKER_PAGE_SIZE && (!hasTotal || fetchedAssetCountRef.current < total));
      } catch {
        if (requestId !== assetRequestIdRef.current) return;
        if (isFirstPage) setAssets([]);
        setHasMoreAssets(false);
      } finally {
        if (requestId === assetRequestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [assetSearch, productId, fetchImages, allowedImageExtensions, acceptedExtensions],
  );

  useEffect(() => {
    if (!show) return;
    if (!showAssetLibrary || (!fetchImages && !productId)) {
      setAssets([]);
      setHasMoreAssets(false);
      return;
    }
    const timer = setTimeout(() => fetchAssetPage(1), assetSearch.trim() ? 500 : 0);
    return () => clearTimeout(timer);
  }, [show, showAssetLibrary, assetSearch, productId, fetchImages, fetchAssetPage]);

  /** Load the next page once the results list is scrolled near its end. */
  const handleAssetScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!hasMoreAssets || loading || loadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= 40) fetchAssetPage(assetPage + 1);
  };

  const handleFileSelect = (selected: File) => {
    revokeOriginalCropSrc();
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
    setSelectedAssetId(null);
    setSelectedAssetUrl(null);
    setSelectedAssetKey(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFileSelect(dropped);
  };

  const resetState = () => {
    revokeCropObjectUrl();
    revokeOriginalCropSrc();
    setCropSrc(null);
    setFile(null);
    setPreviewUrl(null);
    setImageTitle('');
    setAliasName('');
    setAssetSearch('');
    setAssets([]);
    setAssetPage(1);
    setHasMoreAssets(false);
    fetchedAssetCountRef.current = 0;
    setSelectedAssetId(null);
    setSelectedAssetUrl(null);
    setSelectedAssetKey(null);
    setIsCropping(false);
    setAddToDesign(false);
    setAddToAssetLibrary(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  /**
   * Commit the current selection. The design tick only travels when the host asked for the
   * checkbox AND an image is actually selected, so a stale tick can never reach the payload.
   * The asset-library tick additionally never travels with a library-sourced image, which is
   * already an asset and would otherwise be filed a second time.
   */
  const handleSave = () => {
    const isAddToDesign = showAddToDesign === true && addToDesign && Boolean(previewUrl || selectedAssetUrl);
    if (selectedAssetKey) {
      onSave({
        file: null,
        assetUrl: selectedAssetUrl,
        imageTitle,
        aliasName,
        assetKey: selectedAssetKey,
        assetId: selectedAssetId,
        isAssetUpload: true,
        addToDesign: isAddToDesign,
        addToAssetLibrary: false,
      });
    } else {
      onSave({
        file,
        assetUrl: null,
        imageTitle,
        aliasName,
        assetKey: null,
        assetId: null,
        isAssetUpload: false,
        addToDesign: isAddToDesign,
        addToAssetLibrary: showAddToAssetLibrary === true && addToAssetLibrary && Boolean(file),
      });
    }
    handleClose();
  };

  const handleAssetSelect = (asset: Asset, url: string | null) => {
    revokeOriginalCropSrc();
    const isDeselect = asset._id === selectedAssetId;
    if (isDeselect) {
      setSelectedAssetId(null);
      setSelectedAssetUrl(null);
      setSelectedAssetKey(null);
    } else {
      setSelectedAssetId(asset._id);
      setSelectedAssetUrl(url);
      const key = asset.croppedFileUrl || asset.fileUrl || null;
      setSelectedAssetKey(key);
      setFile(null);
      setPreviewUrl(null);
      setImageTitle(asset.title ?? '');
      setAliasName(asset.description ?? '');
    }
  };

  /**
   * Open the crop tool on the current image.
   *
   * Once a crop has been applied the retained original is reopened instead of the visible preview,
   * so re-cropping adjusts the original selection rather than cutting further into the last crop.
   *
   * A locally picked file is already a same-origin `blob:` URL. An asset picked from the library
   * resolves to a pre-signed S3 URL on another origin, which taints the export canvas and makes
   * `toBlob` throw — so its bytes are proxy-fetched through the backend and turned into a
   * same-origin object URL first. With nothing picked yet, the host's existing image is cropped the
   * same way, by its key or URL.
   */
  const handleStartCrop = async () => {
    if (originalCropSrcRef.current) {
      setCropSrc(originalCropSrcRef.current);
      setIsCropping(true);
      return;
    }
    if (previewUrl) {
      setCropSrc(previewUrl);
      setIsCropping(true);
      return;
    }
    const cropSourceKey = selectedAssetKey ?? existingImage ?? null;
    const cropSourceUrl = selectedAssetKey ? selectedAssetUrl : existingImageUrl;
    if (!cropSourceKey) return;
    if (/^(https?:|blob:|data:)/.test(cropSourceKey)) {
      setCropSrc(cropSourceUrl);
      setIsCropping(true);
      return;
    }
    setIsCropLoading(true);
    try {
      const blob = await getS3FileAsBlob({ objectKey: cropSourceKey });
      revokeCropObjectUrl();
      const objectUrl = URL.createObjectURL(blob);
      cropObjectUrlRef.current = objectUrl;
      setCropSrc(objectUrl);
      setIsCropping(true);
    } catch {
      toasterMsg(t('assets.cropLoadFailed'), 'danger');
    } finally {
      setIsCropLoading(false);
    }
  };

  /** Leave the crop tool without applying a crop. */
  const handleCropCancel = () => {
    setIsCropping(false);
    revokeCropObjectUrl();
    setCropSrc(null);
  };

  /**
   * Called by CropTool on save — replace current preview with the cropped result.
   *
   * The image the crop was taken from is retained first, so it survives as the source for a later
   * re-crop; only a superseded preview the retention did not claim is released here.
   *
   * A crop of an image picked from the library produces new bytes, so the selection stops being a
   * library reference and becomes an upload. The tick is cleared with it: re-filing the crop would
   * add a second asset for what the user sees as one picture, and record the usage against the copy
   * rather than the original.
   */
  const handleCropSave = (croppedBlob: Blob) => {
    retainOriginalCropSrc(cropSrc);
    if (previewUrl && previewUrl !== originalCropSrcRef.current) URL.revokeObjectURL(previewUrl);
    revokeCropObjectUrl();
    setCropSrc(null);
    const croppedFile = new File([croppedBlob], file?.name ?? 'cropped.jpg', { type: 'image/jpeg' });
    const wasLibraryImage = Boolean(selectedAssetKey);
    setFile(croppedFile);
    setPreviewUrl(URL.createObjectURL(croppedBlob));
    setSelectedAssetId(null);
    setSelectedAssetUrl(null);
    setSelectedAssetKey(null);
    if (wasLibraryImage) setAddToAssetLibrary(false);
    setIsCropping(false);
  };

  const newImagePreview = previewUrl ?? selectedAssetUrl;
  const displayPreview = newImagePreview ?? existingImageUrl;

  return (
    <BombiixModal
      show={show}
      onClose={handleClose}
      onSave={handleSave}
      saveLabel={t('assets.save') as string}
      title={t('assets.imageUpload') as string}
      className="image-upload-modal-dialog"
    >
      <div className="ium-content">
        <div className="ium-body">
          <div className="ium-left-panel">
            {isCropping && cropSrc ? (
              <CropTool src={cropSrc} onSave={handleCropSave} onCancel={handleCropCancel} />
            ) : (
              <div className="ium-dropzone-wrap">
                <div
                  className={`ium-dropzone${isDragOver ? ' ium-dropzone--active' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragOver(true);
                  }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={handleDrop}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                >
                  {displayPreview ? (
                    <img src={displayPreview} alt="preview" className="ium-preview-img" />
                  ) : (
                    <div className="ium-dropzone-placeholder">
                      <img src={thumbNailImage} alt="" className="upload-dropzone__icon" aria-hidden="true" />
                      <p className="ium-dropzone-hint">
                        <span className="ium-click-link">{t('assets.clickToUpload') as string}</span> {t('assets.orDragAndDrop') as string}
                      </p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="d-none"
                    onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                  />
                </div>
              </div>
            )}
          </div>

          {showAssetLibrary && (
            <div className="ium-form-panel">
              <div className="ium-search-wrap">
                <input
                  type="text"
                  className="form-control form-control-sm ium-search-input"
                  placeholder={t('assets.searchAssetLibrary') as string}
                  value={assetSearch}
                  onChange={(e) => setAssetSearch(e.target.value)}
                />
                <i className="bombiix-icon-search ium-search-icon" aria-hidden="true" />
              </div>

              <div className="ium-asset-results" onScroll={handleAssetScroll}>
                {loading ? (
                  <p className="ium-asset-hint">{t('assets.loading') as string}</p>
                ) : assets.length === 0 ? (
                  <p className="ium-asset-hint">{t('assets.noAssets') as string}</p>
                ) : (
                  <>
                    <div className="ium-asset-grid">
                      {assets.map((asset) => (
                        <AssetThumb key={asset._id} asset={asset} isSelected={selectedAssetId === asset._id} onSelect={handleAssetSelect} />
                      ))}
                    </div>
                    {loadingMore && <p className="ium-asset-hint">{t('assets.loading') as string}</p>}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {!isCropping && (
          <div className="ium-controls-row">
            <button type="button" className="bombiix-btn btn-blue" onClick={() => fileInputRef.current?.click()}>
              {t('assets.uploadImage') as string}
            </button>
            <input
              type="text"
              className="form-control form-control-sm ium-image-title-input"
              placeholder={t('assets.imageTitle') as string}
              value={imageTitle}
              onChange={(e) => setImageTitle(e.target.value)}
            />
            <button type="button" className="ium-crop-btn" aria-label="Crop" disabled={!displayPreview || isCropLoading} onClick={handleStartCrop}>
              <CropIcon size={20} />
            </button>
            {showAddToDesign && (
              <div className="ium-upload-toggle">
                <NewCheckbox
                  id="imageUploadAddToDesign"
                  name="imageUploadAddToDesign"
                  label={t('assets.addToDesignDetails') as string}
                  checked={addToDesign && Boolean(newImagePreview)}
                  disabled={!newImagePreview}
                  onChange={(event) => setAddToDesign(event.target.checked)}
                />
              </div>
            )}
            {showAddToAssetLibrary && (
              <div className="ium-upload-toggle">
                <NewCheckbox
                  id="imageUploadAddToAssetLibrary"
                  name="imageUploadAddToAssetLibrary"
                  label={t('assets.addToAssetLibrary') as string}
                  checked={addToAssetLibrary && Boolean(file)}
                  disabled={!file}
                  onChange={(event) => setAddToAssetLibrary(event.target.checked)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </BombiixModal>
  );
}

export default ImageUploadModal;

/**
 * Whether an image value is an inline base64 data URL, which renders as-is and must never be sent
 * to the S3 signer.
 * @param image - the image value to inspect.
 * @returns true for a `data:` URL.
 */
function isDataUrl(image: string | null | undefined): boolean {
  return Boolean(image?.startsWith('data:'));
}
